import React, { useState, useContext } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Alert,
  Spinner,
} from "react-bootstrap";
import { useCart } from "../context/CartContext";
import { AuthContext } from "../context/AuthContext";
import { CountryContext } from "../context/CountryContext";
import { Link, useNavigate } from "react-router-dom";
import { FaTrash } from "react-icons/fa";
import { AiFillWarning } from "react-icons/ai";

import CheckoutModal from "../components/CheckoutModal";
import SuccessModal from "../components/SuccessModal";
import ConfirmModal from "../components/ConfirmModal";

import "../styles/Cart.css";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const currencySymbols = { US: '$', UK: '£', SL: 'LKR' };

const Cart = () => {
  const navigate = useNavigate();
  const {
    cart,
    summary,
    loading,
    error,
    updateQuantity,
    removeFromCart,
    clearCart,
    clearError,
    formatPrice,
    getCurrentCurrency,
  } = useCart();

  const { userData, isLoggedIn } = useContext(AuthContext);
  const { country } = useContext(CountryContext);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [updatingItems, setUpdatingItems] = useState(new Set());
  const [removingItems, setRemovingItems] = useState(new Set());
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalTitle, setModalTitle] = useState("");

  // Get available cart items
  const availableCartItems = cart.filter(
    (item) =>
      item.stock_qty === null ||
      item.stock_qty === undefined ||
      item.stock_qty > 0
  );

  // Handle individual item selection
  const handleItemSelection = (cart_id, isSelected) => {
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(cart_id);
      } else {
        newSet.delete(cart_id);
      }
      return newSet;
    });
  };

  // Handle select all toggle
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems(new Set());
    } else {
      const availableItemIds = availableCartItems.map((item) => item.cart_id);
      setSelectedItems(new Set(availableItemIds));
    }
    setSelectAll(!selectAll);
  };

  // Update selectAll state when individual items are selected/deselected
  React.useEffect(() => {
    const availableItemIds = availableCartItems.map((item) => item.cart_id);
    const allSelected =
      availableItemIds.length > 0 &&
      availableItemIds.every((id) => selectedItems.has(id));
    setSelectAll(allSelected);
  }, [selectedItems, availableCartItems]);

  // Calculate summary for selected items only
  const selectedItemsSummary = React.useMemo(() => {
    const selectedCartItems = cart.filter((item) =>
      selectedItems.has(item.cart_id)
    );
    const totalItems = selectedCartItems.reduce(
      (sum, item) => sum + item.quantity,
      0
    );
    const totalAmount = selectedCartItems.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );

    return {
      total_items: totalItems,
      total_amount: totalAmount.toFixed(2),
      currency_symbol: summary.currency_symbol || "$",
      selected_count: selectedCartItems.length,
    };
  }, [cart, selectedItems, summary.currency_symbol]);

  const handleQuantityChange = async (cart_id, newQuantity) => {
    if (newQuantity < 1) return;

    // Find the item to check stock
    const item = cart.find((item) => item.cart_id === cart_id);
    // Only check stock limit if stock_qty is defined
    if (
      item &&
      item.stock_qty !== null &&
      item.stock_qty !== undefined &&
      newQuantity > item.stock_qty
    ) {
      setModalTitle("Stock Limit Exceeded");
      setModalMessage(`Only ${item.stock_qty} items available in stock.`);
      setShowErrorModal(true);
      return;
    }

    setUpdatingItems((prev) => new Set(prev).add(cart_id));

    try {
      const result = await updateQuantity(cart_id, newQuantity);
      if (!result.success) {
        setModalTitle("Update Failed");
        setModalMessage(result.message || "Failed to update quantity");
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Error updating quantity:", error);
    } finally {
      setUpdatingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(cart_id);
        return newSet;
      });
    }
  };

  const handleRemoveItem = async (cart_id) => {
    setRemovingItems((prev) => new Set(prev).add(cart_id));

    try {
      const result = await removeFromCart(cart_id);
      if (!result.success) {
        console.error("Failed to remove item:", result.message);
      }
    } catch (error) {
      console.error("Error removing item:", error);
    } finally {
      setRemovingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(cart_id);
        return newSet;
      });
    }
  };

  const [showClearCartModal, setShowClearCartModal] = useState(false);

  const handleClearCart = () => {
    setShowClearCartModal(true);
  };

  const confirmClearCart = async () => {
    try {
      const result = await clearCart();
      if (!result.success) {
        setModalTitle("Error");
        setModalMessage("Failed to clear cart.");
        setShowErrorModal(true);
      } else {
        setModalTitle("Success");
        setModalMessage("Cart cleared successfully.");
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error("Error clearing cart:", error);
      setModalTitle("Error");
      setModalMessage("An unexpected error occurred while clearing the cart.");
      setShowErrorModal(true);
    } finally {
      setShowClearCartModal(false);
    }
  };

  const handleBuyNow = () => {
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }

    if (selectedItems.size === 0) {
      setModalTitle("No Items Selected");
      setModalMessage(
        "Please select at least one item to proceed to checkout."
      );
      setShowErrorModal(true);
      return;
    }

    setShowCheckoutModal(true);
  };

  // Helper function to format individual item prices
  const formatItemPrice = (price) => {
    return formatPrice(price);
  };

  // Helper function to format total price for an item
  const formatItemTotal = (price, quantity) => {
    return formatPrice(price * quantity);
  };

  // Helper function to format order summary prices with conversion
  const formatSummaryPrice = (amount) => {
    const { rate, symbol } = getCurrentCurrency();
    const convertedAmount = Number(amount) * rate;
    return `${symbol}${convertedAmount.toFixed(2)}`;
  };

  // handle click on image
  const handleRedirect = (styleId) => {
    navigate(`/product/${styleId}`);
  };

  if (loading) {
    return (
      <div className="cart-container">
        <Container className="my-5 text-center">
          <Spinner animation="border" role="status" size="lg">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3 h5">Loading your cart...</p>
        </Container>
      </div>
    );
  }

  return (
    <div className="cart-container">
      <Container className="my-4">
        <Row>
          <Col>
            {/* Compact Header */}
            <div className="cart-header mb-4">
              <div className="d-flex align-items-center justify-content-between">
                <h3 className="mb-1">🛒 Shopping Cart</h3>
                {isLoggedIn && userData?.name && (
                  <small className="text-muted">
                    Welcome back, <strong>{userData.name}</strong>! 👋
                  </small>
                )}
              </div>
            </div>

            {error && (
              <Alert
                variant="danger"
                dismissible
                onClose={clearError}
                className="mb-3"
              >
                <strong>Error:</strong> {error}
              </Alert>
            )}

            {cart.length === 0 ? (
              <Card className="text-center py-5 border-0 shadow-sm">
                <Card.Body>
                  <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>
                    🛍️
                  </div>
                  <h4 className="mb-3">Your cart is empty</h4>
                  <p className="text-muted mb-4">
                    Discover amazing products and start shopping today!
                  </p>
                  <Link to="/">
                    <Button variant="primary" size="lg">
                      🌟 Start Shopping
                    </Button>
                  </Link>
                </Card.Body>
              </Card>
            ) : (
              <Row className="g-4">
                <Col lg={8}>
                  {/* Compact Select All */}
                  {availableCartItems.length > 0 && (
                    <div className="select-all-bar">
                      <Form.Check
                        type="checkbox"
                        id="select-all"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        label={
                          <span className="fw-semibold">
                            Select All ({availableCartItems.length})
                          </span>
                        }
                      />
                      <small className="text-muted">
                        {selectedItems.size} selected
                      </small>
                    </div>
                  )}

                  {/* Modern Cart Items */}
                  <div className="cart-items-list">
                    {cart.map((item) => (
                      <div
                        key={item.cart_id}
                        className={`cart-item-modern mb-3 p-3 border rounded-3 bg-white shadow-sm ${
                          removingItems.has(item.cart_id) ? "removing" : ""
                        } ${
                          selectedItems.has(item.cart_id)
                            ? "border-primary"
                            : ""
                        }`}
                      >
                        <div className="row align-items-center">
                          {/* Checkbox & Image */}
                          <div className="col-md-3 d-flex align-items-center">
                            <div className="me-3">
                              {item.stock_qty === null ||
                              item.stock_qty === undefined ||
                              item.stock_qty > 0 ? (
                                <Form.Check
                                  type="checkbox"
                                  id={`select-${item.cart_id}`}
                                  checked={selectedItems.has(item.cart_id)}
                                  onChange={(e) =>
                                    handleItemSelection(
                                      item.cart_id,
                                      e.target.checked
                                    )
                                  }
                                  disabled={
                                    updatingItems.has(item.cart_id) ||
                                    removingItems.has(item.cart_id)
                                  }
                                  className="cart-checkbox"
                                />
                              ) : (
                                <div className="text-warning">
                                  <AiFillWarning />
                                </div>
                              )}
                            </div>
                            <div
                              className="product-image-wrapper"
                              style={{
                                width: "80px",
                                height: "80px",
                                cursor: "pointer",
                              }}
                              onClick={() => handleRedirect(item.style_number)}
                            >
                              <img
                                src={`${BASE_URL}/uploads/styles/${item.image}`}
                                alt={item.style_name}
                                className="img-fluid rounded"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            </div>
                          </div>

                          {/* Product Info */}
                          <div className="col-md-4">
                            <h6 className="mb-2 fw-semibold">
                              {item.product_name}
                            </h6>
                            <div className="product-details-compact">
                              {(() => {
                                const details = [];
                                
                                if (item.color_name && item.color_name !== "Not Selected" && item.color_name !== "N/A" && item.color_name.trim()) {
                                  details.push(
                                    <div key="color" className="att-details">
                                      Color: <small>{item.color_name}</small>
                                    </div>
                                  );
                                }
                                
                                if (item.size_name && item.size_name !== "Not Selected" && item.size_name !== "N/A" && item.size_name.trim()) {
                                  details.push(
                                    <div key="size" className="att-details">
                                      Size: <small>{item.size_name}</small>
                                    </div>
                                  );
                                }
                                
                                if (item.material_name) {
                                  details.push(
                                    <div key="material" className="att-details">
                                      Material: <small>{item.material_name}</small>
                                    </div>
                                  );
                                }
                                
                                return details.length > 0 ? details : null;
                              })()}
                            </div>
                            {item.stock_qty !== null &&
                              item.stock_qty !== undefined &&
                              item.stock_qty <= 5 &&
                              item.stock_qty > 0 && (
                                <small className="text-warning">
                                  <AiFillWarning /> Only {item.stock_qty} left
                                </small>
                              )}
                            {item.stock_qty === 0 && (
                              <small className="text-danger">
                                Out of stock
                              </small>
                            )}
                          </div>

                          {/* Price & Quantity */}
                          <div className="col-md-3">
                            <div className="text-center">
                              <div className="fw-bold text-primary mb-2">
                                {formatItemPrice(item.price)}
                              </div>
                              <div className="quantity-controls-modern d-flex align-items-center justify-content-center">
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  className="px-2 py-1"
                                  onClick={() =>
                                    handleQuantityChange(
                                      item.cart_id,
                                      item.quantity - 1
                                    )
                                  }
                                  disabled={
                                    item.quantity <= 1 ||
                                    updatingItems.has(item.cart_id) ||
                                    removingItems.has(item.cart_id)
                                  }
                                >
                                  −
                                </Button>
                                <span className="mx-3 fw-semibold">
                                  {item.quantity}
                                </span>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  className="px-2 py-1"
                                  onClick={() =>
                                    handleQuantityChange(
                                      item.cart_id,
                                      item.quantity + 1
                                    )
                                  }
                                  disabled={
                                    (item.stock_qty !== null &&
                                      item.stock_qty !== undefined &&
                                      item.quantity >= item.stock_qty) ||
                                    updatingItems.has(item.cart_id) ||
                                    removingItems.has(item.cart_id)
                                  }
                                >
                                  +
                                </Button>
                              </div>
                              {updatingItems.has(item.cart_id) && (
                                <small className="text-primary d-block mt-1">
                                  <Spinner
                                    animation="border"
                                    size="sm"
                                    className="me-1"
                                  />
                                  Updating...
                                </small>
                              )}
                            </div>
                          </div>

                          {/* Total & Actions */}
                          <div className="col-md-2 text-end">
                            <div
                              className="fw-bold mb-2"
                              style={{ fontSize: "1.1rem" }}
                            >
                              {formatItemTotal(item.price, item.quantity)}
                            </div>
                            <FaTrash
                              className="cart-remove-btn"
                              onClick={() => handleRemoveItem(item.cart_id)}
                              disabled={
                                updatingItems.has(item.cart_id) ||
                                removingItems.has(item.cart_id)
                              }
                            >
                              {removingItems.has(item.cart_id) ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                "🗑️"
                              )}
                            </FaTrash>
                          </div>
                        </div>

                        {/* Stock Warnings */}
                        {item.stock_qty !== null &&
                          item.stock_qty !== undefined &&
                          item.stock_qty < 6 &&
                          item.stock_qty > 0 && (
                            <div className="mt-2">
                              <Alert variant="warning" className="mb-0 py-2">
                                <small>
                                  <AiFillWarning /> Only {item.stock_qty} items
                                  left in stock!
                                </small>
                              </Alert>
                            </div>
                          )}
                        {item.stock_qty === 0 && (
                          <div className="mt-2">
                            <Alert variant="danger" className="mb-0 py-2">
                              <small>This item is currently out of stock</small>
                            </Alert>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Clear Cart Button */}
                  <div className="text-center mt-4">
                    <Button
                      variant="outline-danger"
                      onClick={handleClearCart}
                      disabled={cart.length === 0}
                    >
                      <FaTrash /> Clear Cart
                    </Button>
                  </div>
                </Col>

                {/* Modern Sidebar Summary */}
                <Col lg={4}>
                  <div className="position-sticky" style={{ top: "20px" }}>
                    <Card className="border-0 shadow">
                      <Card.Header className="cart-summary-header">
                        <h6 className="order-summary-title">
                          📋 Order Summary
                        </h6>
                      </Card.Header>
                      <Card.Body className="p-4">
                        {/* Cart Overview */}
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <small className="text-muted">Total Items:</small>
                            <span className="fw-semibold">
                              {summary.total_items}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between align-items-center">
                            <small className="text-muted">Cart Total:</small>
                            <span className="fw-semibold">
                              {formatSummaryPrice(summary.original_amount || summary.total_amount)}
                            </span>
                          </div>
                        </div>

                        {selectedItems.size > 0 && (
                          <>
                            <hr className="my-3" />
                            <div className="mb-3">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="text-success">
                                  ✅ Selected:
                                </span>
                                <span className="fw-bold text-success">
                                  {selectedItemsSummary.selected_count} items
                                </span>
                              </div>
                              <div className="d-flex justify-content-between align-items-center">
                                <span>Selected Total:</span>
                                <span className="fw-bold">
                                  {formatSummaryPrice(selectedItemsSummary.total_amount)}
                                </span>
                              </div>
                            </div>
                          </>
                        )}

                        {isLoggedIn && (
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <span className="text-success">💸 Discount:</span>
                            <span className="fw-bold text-success">
                              {summary.discount_amount > 0
                                ? `-${formatSummaryPrice(summary.discount_amount)}`
                                : "00.00"}
                            </span>
                          </div>
                        )}

                        {selectedItems.size > 0 && (
                          <>
                            <hr className="my-3" />
                            <div className="d-flex justify-content-between align-items-center mb-4">
                              <strong>💰 Checkout Total:</strong>
                              <strong className="fs-5">
                                {formatSummaryPrice(selectedItemsSummary.total_amount)}
                              </strong>
                            </div>
                          </>
                        )}

                        {selectedItems.size === 0 && (
                          <div className="text-center mb-3 p-3 bg-light rounded">
                            <small className="text-muted">
                              Select items to proceed to checkout
                            </small>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="d-grid gap-2">
                          <Button
                            variant="primary"
                            size="lg"
                            disabled={
                              selectedItems.size === 0 ||
                              cart.length === 0 ||
                              cart.some(
                                (item) =>
                                  selectedItems.has(item.cart_id) &&
                                  item.stock_qty === 0
                              )
                            }
                            onClick={() => handleBuyNow()}
                            className="checkout-btn"
                          >
                            🚀 Checkout ({selectedItems.size})
                          </Button>

                          <CheckoutModal
                            show={showCheckoutModal}
                            isDirectBuy={false}
                            selectedItems={Array.from(selectedItems)}
                            selectedItemsSummary={selectedItemsSummary}
                            onHide={() => setShowCheckoutModal(false)}
                          />

                          <Link to="/" className="d-grid">
                            <Button variant="outline-secondary" size="lg">
                              🛍️ Continue Shopping
                            </Button>
                          </Link>
                        </div>

                        {summary.out_of_stock_items > 0 && (
                          <div className="mt-3 p-3 bg-warning bg-opacity-10 rounded">
                            <small className="text-warning">
                              <AiFillWarning /> {summary.out_of_stock_items}{" "}
                              item(s) unavailable.
                              {selectedItems.size > 0
                                ? " Selected items ready for checkout."
                                : " Select available items to proceed."}
                            </small>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </div>
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      </Container>

      {/* Clear Cart Confirmation Modal */}
      <ConfirmModal
        show={showClearCartModal}
        onHide={() => setShowClearCartModal(false)}
        onConfirm={confirmClearCart}
        title="Clear Cart"
        message="Are you sure you want to remove all items from your cart?"
        confirmText="Yes, Clear Cart"
        cancelText="Cancel"
        confirmVariant="danger"
        icon="🗑️"
      />

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        title={modalTitle}
        message={modalMessage}
        variant="success"
      />

      {/* Error Modal */}
      <SuccessModal
        show={showErrorModal}
        onHide={() => setShowErrorModal(false)}
        title={modalTitle}
        message={modalMessage}
        variant="error"
      />
    </div>
  );
};

export default Cart;
